import {
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  InputAdornment,
  LinearProgress,
  Radio,
  RadioGroup,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  useMediaQuery
} from '@material-ui/core'
import i18n from 'i18next'
import CheckCircleOutlineIcon from 'mdi-react/CheckCircleOutlineIcon'
import OpenInNewIcon from 'mdi-react/OpenInNewIcon'
import { CSSProperties, FC, ReactNode, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { closeDialog } from '../../../actions/dialogs'
import { selectApartmentFilesByApartmentId } from '../../../selectors/apartments'
import { selectIsMobile } from '../../../selectors/users'
import apartmentAPI from '../../../services/apartments'
import { generateSaleDescription } from '../../../services/openAI'
import { Apartment } from '../../../types/apartments'
import Dialog from '../../Dialog'
import { getFiksumyyntiAppUrl, getFiksumyyntiListingUrl } from './fiksumyyntiUrl'
import HousingCompanySearchField from './HousingCompanySearchField'

export const SELL_ON_FIKSUMYYNTI_DIALOG_ID = 'SellOnFiksumyynti'

const KEY = 'apartmentOverview.sellOnFiksumyynti'
const STEP_KEYS = ['price', 'company', 'description', 'review']
const GREEN = '#4f9b44'
const MUTED = 'rgba(0,0,0,.54)'

interface SellFormValues {
  salePrice: string
  debtShare: string
  maintenanceFee: string
  financingFee: string
  housingCompanyName: string
  housingCompanyBusinessId: string
  lotOwnership: string
  buildingLetter: string
  propertyManager: string
}

type FormErrors = Partial<Record<keyof SellFormValues | 'description' | 'accepts', string>>

const parseNumber = (value: string): number => Number(value.replace(',', '.').replace(/\s/g, ''))

const formatEur = (value: number): string =>
  `${value.toLocaleString('fi-FI', { maximumFractionDigits: 2 })} €`

const box: CSSProperties = {
  border: '1px solid rgba(0,0,0,.125)',
  borderRadius: 4,
  padding: '12px 14px',
  display: 'flex',
  flexDirection: 'column',
  gap: 6
}
const boxTitle: CSSProperties = { fontSize: 14, textTransform: 'uppercase', color: 'rgba(0,0,0,.87)' }
const sidePanel: CSSProperties = { background: '#F1F2F4', padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }
const sideTitle: CSSProperties = { fontSize: '0.8rem', textTransform: 'uppercase', color: '#7B8189', letterSpacing: '0.02857em' }

export interface SellOnFiksumyyntiDialogProps {
  t: i18n.TFunction
  apartment: Apartment
  onClose?: () => void
  onPublished?: () => void
}

/**
 * Ristiinmyynti: neljän vaiheen dialogi (hinta, taloyhtiö, AI-esittelyteksti, tarkistus),
 * josta myynti-ilmoitus julkaistaan suoraan Fiksumyyntiin. Muut tiedot ja kuvat
 * kopioidaan vuokrailmoituksesta backendissä (cross-create).
 */
const SellOnFiksumyyntiDialog: FC<SellOnFiksumyyntiDialogProps> = ({ t, apartment, onClose, onPublished }) => {
  const dispatch = useDispatch()
  // Neljän vaiheen stepper ja sivupaneelit tarvitsevat levyttä; kapeammilla näytöillä kompakti näkymä
  const isNarrow = useMediaQuery('(max-width:959px)')
  const isMobile = useSelector(selectIsMobile) || isNarrow
  const files = useSelector(selectApartmentFilesByApartmentId(apartment.apartmentId))
  const information: any = apartment.information || {}

  const [step, setStep] = useState(0)
  const [values, setValues] = useState<SellFormValues>({
    salePrice: '',
    debtShare: '',
    maintenanceFee: '',
    financingFee: '',
    housingCompanyName: '',
    housingCompanyBusinessId: '',
    lotOwnership: '',
    buildingLetter: [information.buildingLetter, information.apartmentNumber].filter(Boolean).join(' '),
    propertyManager: information.propertyManager || ''
  })
  const [errors, setErrors] = useState<FormErrors>({})
  const [description, setDescription] = useState('')
  const [descriptionRequested, setDescriptionRequested] = useState(false)
  const [descriptionLoading, setDescriptionLoading] = useState(false)
  const [acceptFee, setAcceptFee] = useState(false)
  const [acceptTerms, setAcceptTerms] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitFailed, setSubmitFailed] = useState(false)
  const [published, setPublished] = useState<{ code?: string; alreadyLinked: boolean } | null>(null)

  const fmUrl = getFiksumyyntiAppUrl()
  const salePrice = parseNumber(values.salePrice)
  const debtShare = values.debtShare.trim() ? parseNumber(values.debtShare) : 0
  const debtFreePrice = salePrice > 0 && debtShare >= 0 ? salePrice + debtShare : undefined
  const imageCount = (apartment.media || []).length
  const coverKey = (apartment.media || [])[0]?.key
  const coverUrl = ((files || []) as any[]).find(file => file.key === coverKey && file.url)?.url
  const roomsLabel = [
    information.rooms ? `${information.rooms}h` : '',
    information.kitchen ? String(information.kitchen).toLowerCase() : ''
  ].filter(Boolean).join(' + ')
  const areaLabel = information.squareMeters ? `${String(information.squareMeters).replace('.', ',')} m²` : ''
  const streetLabel = [information.streetAddress, information.streetNumber].filter(Boolean).join(' ')

  const setValue = (field: keyof SellFormValues, value: string) => {
    setValues(prev => ({ ...prev, [field]: value }))
    setErrors(prev => ({ ...prev, [field]: undefined }))
  }

  const handleClose = () => {
    dispatch(closeDialog(SELL_ON_FIKSUMYYNTI_DIALOG_ID))
    if (onClose) {
      onClose()
    }
  }

  const requestDescription = async () => {
    if (descriptionRequested) {
      return
    }
    setDescriptionRequested(true)
    setDescriptionLoading(true)
    const generated = await generateSaleDescription(information)
    setDescription(prev => prev || generated || information.notes || '')
    setDescriptionLoading(false)
  }

  const validate = (current: number): boolean => {
    const required = t(`${KEY}.required`)
    const invalid = t(`${KEY}.invalidNumber`)
    const next: FormErrors = {}
    if (current === 0) {
      if (!values.salePrice.trim()) next.salePrice = required
      else if (!(salePrice > 0)) next.salePrice = invalid
      if (values.debtShare.trim() && !(debtShare >= 0)) next.debtShare = invalid
      if (!values.maintenanceFee.trim()) next.maintenanceFee = required
      else if (!(parseNumber(values.maintenanceFee) >= 0)) next.maintenanceFee = invalid
      if (values.financingFee.trim() && !(parseNumber(values.financingFee) >= 0)) next.financingFee = invalid
    }
    if (current === 1) {
      if (!values.housingCompanyName.trim()) next.housingCompanyName = required
      if (!values.lotOwnership) next.lotOwnership = required
      if (!values.buildingLetter.trim()) next.buildingLetter = required
    }
    if (current === 2 && !description.trim()) {
      next.description = required
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const goNext = () => {
    if (!validate(step)) {
      return
    }
    // Luonnos käynnistetään jo taloyhtiövaiheesta, jotta se on valmis vaiheeseen 3 tultaessa
    if (step <= 1) {
      requestDescription()
    }
    setStep(step + 1)
  }

  const handlePublish = async () => {
    setSubmitting(true)
    setSubmitFailed(false)
    try {
      const saleInformation: { [key: string]: number | string } = {
        salePrice,
        debtFreePrice: debtFreePrice || salePrice,
        maintenanceFee: parseNumber(values.maintenanceFee),
        housingCompanyName: values.housingCompanyName.trim(),
        lotOwnership: values.lotOwnership,
        buildingLetter: values.buildingLetter.trim(),
        notes: description.trim()
      }
      if (values.financingFee.trim()) {
        saleInformation.financingFee = parseNumber(values.financingFee)
      }
      if (values.housingCompanyBusinessId) {
        saleInformation.housingCompanyBusinessId = values.housingCompanyBusinessId
      }
      if (values.propertyManager.trim()) {
        saleInformation.propertyManagerContact = values.propertyManager.trim()
      }
      const response = await apartmentAPI.crossCreateApartment(apartment.userId, apartment.apartmentId, {
        targetProductType: 'smartsale',
        information: saleInformation,
        publish: true,
        confirmation: { confirm: true, serviceFeeCommitment: true }
      })
      setPublished({ code: response.apartment?.code, alreadyLinked: response.alreadyLinked })
      if (onPublished) {
        onPublished()
      }
    } catch (error) {
      setSubmitFailed(true)
    }
    setSubmitting(false)
  }

  const moneyField = (field: keyof SellFormValues, unit: string, required: boolean) => (
    <TextField
      fullWidth
      variant='outlined'
      size='small'
      label={t(`${KEY}.fields.${field}`)}
      value={values[field]}
      onChange={event => setValue(field, event.target.value)}
      required={required}
      error={Boolean(errors[field])}
      helperText={errors[field] || ''}
      inputProps={{ inputMode: 'decimal' }}
      InputProps={{ endAdornment: <InputAdornment position='end'>{unit}</InputAdornment> }}
    />
  )

  const checkRow = (text: ReactNode, key: string) => (
    <div key={key} style={{ display: 'flex', gap: 8, fontSize: 14, lineHeight: 1.43 }}>
      <CheckCircleOutlineIcon size={18} color={GREEN} style={{ flex: 'none' }} />
      <span>{text}</span>
    </div>
  )

  const summaryRow = (label: string, value: string) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
      <span style={{ fontSize: 14 }}>{label}</span>
      <span style={{ fontSize: 15, color: MUTED, textAlign: 'right' }}>{value}</span>
    </div>
  )

  const previewCard = (imageHeight: number) => (
    <div style={{ background: '#fff', borderRadius: 4, overflow: 'hidden', boxShadow: '0 2px 1px -1px rgba(0,0,0,.2), 0 1px 1px 0 rgba(0,0,0,.14), 0 1px 3px 0 rgba(0,0,0,.12)' }}>
      {coverUrl ? (
        <img src={coverUrl} alt='' style={{ display: 'block', width: '100%', height: imageHeight, objectFit: 'cover' }} />
      ) : (
        <div style={{ height: imageHeight, background: '#E7ECEF' }} />
      )}
      <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontFamily: 'CircularBold', fontSize: 16 }}>{streetLabel}</span>
        <span style={{ fontSize: 13, color: MUTED }}>
          {[information.buildingType && t(`apartment.buildingType.${information.buildingType}`), roomsLabel, areaLabel]
            .filter(Boolean)
            .join(' · ')}
        </span>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginTop: 4 }}>
          <span style={{ fontFamily: 'CircularBold', fontSize: 18 }}>{salePrice > 0 ? formatEur(salePrice) : '— €'}</span>
          {debtFreePrice !== undefined && (
            <span style={{ fontSize: 12, color: MUTED }}>{t(`${KEY}.preview.debtFree`, { price: formatEur(debtFreePrice) })}</span>
          )}
        </div>
      </div>
    </div>
  )

  const heading = (titleKey: string, subtitleKey?: string) => (
    <div>
      <Typography variant='h5' style={{ marginBottom: 6 }}>{t(titleKey)}</Typography>
      {subtitleKey && <Typography variant='body1' style={{ color: MUTED }}>{t(subtitleKey)}</Typography>}
    </div>
  )

  const priceStep = (
    <Grid container spacing={4}>
      <Grid item xs={12} md={7} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {heading(`${KEY}.price.title`, `${KEY}.price.subtitle`)}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>{moneyField('salePrice', '€', true)}</Grid>
          <Grid item xs={12} sm={6}>{moneyField('debtShare', '€', false)}</Grid>
          <Grid item xs={12}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, background: '#f5faf4', border: `1px solid ${GREEN}`, borderRadius: 4, padding: '10px 14px' }}>
              <span style={{ fontSize: 14 }}>{t(`${KEY}.price.debtFreeTotal`)}</span>
              <span style={{ fontFamily: 'CircularBold', fontSize: 18 }}>{debtFreePrice !== undefined ? formatEur(debtFreePrice) : '— €'}</span>
            </div>
          </Grid>
          <Grid item xs={12} sm={6}>{moneyField('maintenanceFee', t(`${KEY}.units.eurMonth`), true)}</Grid>
          <Grid item xs={12} sm={6}>{moneyField('financingFee', t(`${KEY}.units.eurMonth`), false)}</Grid>
        </Grid>
      </Grid>
      {!isMobile && (
        <Grid item xs={12} md={5}>
          <div style={sidePanel}>
            {previewCard(170)}
            <Typography variant='body2' style={{ color: MUTED, fontSize: 13 }}>{t(`${KEY}.preview.apartmentNumberHidden`)}</Typography>
          </div>
        </Grid>
      )}
    </Grid>
  )

  const companyStep = (
    <Grid container spacing={4}>
      <Grid item xs={12} md={7} style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
        {heading(`${KEY}.company.title`, `${KEY}.company.subtitle`)}
        <HousingCompanySearchField
          label={t(`${KEY}.fields.housingCompanyName`)}
          value={values.housingCompanyName}
          error={Boolean(errors.housingCompanyName)}
          helperText={errors.housingCompanyName}
          manualEntryLabel={t(`${KEY}.company.manualEntry`)}
          onChange={name => setValue('housingCompanyName', name)}
          onSelect={company => setValues(prev => ({ ...prev, housingCompanyBusinessId: company?.businessId || '' }))}
        />
        <FormControl error={Boolean(errors.lotOwnership)} required>
          <FormLabel style={{ fontSize: 14 }}>{t(`${KEY}.fields.lotOwnership`)}</FormLabel>
          <RadioGroup row value={values.lotOwnership} onChange={event => setValue('lotOwnership', event.target.value)}>
            <FormControlLabel value='own' control={<Radio color='primary' />} label={t(`${KEY}.lotOwnershipOptions.own`)} />
            <FormControlLabel value='rented' control={<Radio color='primary' />} label={t(`${KEY}.lotOwnershipOptions.rented`)} />
          </RadioGroup>
          {errors.lotOwnership && <FormHelperText>{errors.lotOwnership}</FormHelperText>}
        </FormControl>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              variant='outlined'
              size='small'
              required
              label={t(`${KEY}.fields.buildingLetter`)}
              placeholder={t(`${KEY}.company.buildingLetterPlaceholder`)}
              value={values.buildingLetter}
              onChange={event => setValue('buildingLetter', event.target.value)}
              error={Boolean(errors.buildingLetter)}
              helperText={
                errors.buildingLetter ||
                (information.buildingLetter || information.apartmentNumber ? t(`${KEY}.company.fromRental`) : '')
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              variant='outlined'
              size='small'
              label={t(`${KEY}.fields.propertyManager`)}
              value={values.propertyManager}
              onChange={event => setValue('propertyManager', event.target.value)}
              helperText={t(`${KEY}.company.optional`)}
            />
          </Grid>
        </Grid>
      </Grid>
      {!isMobile && (
        <Grid item xs={12} md={5}>
          <div style={sidePanel}>
            <span style={sideTitle}>{t(`${KEY}.company.whyTitle`)}</span>
            <Typography variant='body2'>{t(`${KEY}.company.why`)}</Typography>
            <div style={{ height: 1, background: '#E5E5E5' }} />
            <span style={sideTitle}>{t(`${KEY}.company.laterTitle`)}</span>
            <Typography variant='body2'>{t(`${KEY}.company.later`)}</Typography>
          </div>
        </Grid>
      )}
    </Grid>
  )

  const descriptionStep = (
    <Grid container spacing={4}>
      <Grid item xs={12} md={8} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {heading(`${KEY}.description.title`, `${KEY}.description.subtitle`)}
        {descriptionLoading ? (
          <div style={{ border: `2px solid ${GREEN}`, borderRadius: 4, minHeight: 240, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, color: MUTED }}>
            <CircularProgress size={22} />
            <span>{t(`${KEY}.description.generating`)}</span>
          </div>
        ) : (
          <TextField
            fullWidth
            multiline
            rows={isMobile ? 11 : 12}
            variant='outlined'
            label={t(`${KEY}.description.label`)}
            value={description}
            onChange={event => {
              setDescription(event.target.value)
              setErrors(prev => ({ ...prev, description: undefined }))
            }}
            error={Boolean(errors.description)}
            helperText={errors.description || ''}
          />
        )}
      </Grid>
      {!isMobile && (
        <Grid item xs={12} md={4}>
          <div style={sidePanel}>
            <span style={sideTitle}>{t(`${KEY}.description.basisTitle`)}</span>
            {['notes', 'location', 'apartment', 'features', 'renovations'].map(key =>
              checkRow(t(`${KEY}.description.basis.${key}`), key)
            )}
          </div>
          <Typography variant='body2' style={{ color: MUTED, fontSize: 13, marginTop: 12 }}>
            {t(`${KEY}.description.priceNote`)}
          </Typography>
        </Grid>
      )}
    </Grid>
  )

  const acceptance = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <FormControlLabel
        style={{ alignItems: 'flex-start', marginLeft: -9 }}
        control={<Checkbox color='primary' size='small' checked={acceptFee} onChange={(_, checked) => setAcceptFee(checked)} style={{ paddingTop: 2 }} />}
        label={<span style={{ fontSize: 13, lineHeight: 1.5 }}>{t(`${KEY}.serviceFeeCommitment`)} *</span>}
      />
      <FormControlLabel
        style={{ alignItems: 'flex-start', marginLeft: -9 }}
        control={<Checkbox color='primary' size='small' checked={acceptTerms} onChange={(_, checked) => setAcceptTerms(checked)} style={{ paddingTop: 2 }} />}
        label={
          <span style={{ fontSize: 13, lineHeight: 1.5 }}>
            {t(`${KEY}.acceptTermsPrefix`)}{' '}
            <a href={`${fmUrl}/terms-of-service`} target='_blank' rel='noopener noreferrer' style={{ color: GREEN }}>
              {t(`${KEY}.termsLink`)}
            </a>{' '}
            {t(`${KEY}.acceptTermsAnd`)}{' '}
            <a href={`${fmUrl}/privacy-policy`} target='_blank' rel='noopener noreferrer' style={{ color: GREEN }}>
              {t(`${KEY}.privacyLink`)}
            </a>
            . *
          </span>
        }
      />
      {submitFailed && (
        <Typography variant='body2' color='error'>{t(`${KEY}.failed`)}</Typography>
      )}
      <Button
        variant='contained'
        color='primary'
        fullWidth
        disabled={!acceptFee || !acceptTerms || submitting}
        onClick={handlePublish}
        style={{ height: 42, marginTop: 4 }}
      >
        {submitting ? <CircularProgress size={22} style={{ color: 'white' }} /> : t(`${KEY}.publish`)}
      </Button>
      <Typography variant='body2' style={{ color: MUTED, fontSize: 12, textAlign: 'center' }}>
        {t(`${KEY}.review.channels`)}
      </Typography>
    </div>
  )

  const reviewStep = (
    <Grid container spacing={3}>
      <Grid item xs={12} md={7} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Typography variant='h5'>{t(`${KEY}.review.title`)}</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <div style={box}>
              <span style={boxTitle}>{t(`${KEY}.review.price`)}</span>
              {summaryRow(t(`${KEY}.fields.salePrice`), formatEur(salePrice))}
              {summaryRow(t(`${KEY}.review.debtFreePrice`), formatEur(debtFreePrice || salePrice))}
              {summaryRow(t(`${KEY}.fields.maintenanceFee`), `${formatEur(parseNumber(values.maintenanceFee))}/kk`)}
              {values.financingFee.trim() &&
                summaryRow(t(`${KEY}.fields.financingFee`), `${formatEur(parseNumber(values.financingFee))}/kk`)}
            </div>
          </Grid>
          <Grid item xs={12} sm={6}>
            <div style={box}>
              <span style={boxTitle}>{t(`${KEY}.review.company`)}</span>
              <span style={{ fontSize: 14 }}>{values.housingCompanyName}</span>
              {summaryRow(t(`${KEY}.review.apartment`), values.buildingLetter)}
              {summaryRow(t(`${KEY}.fields.lotOwnership`), t(`${KEY}.lotOwnershipOptions.${values.lotOwnership}`))}
              {values.propertyManager.trim() && summaryRow(t(`${KEY}.fields.propertyManager`), values.propertyManager)}
            </div>
          </Grid>
        </Grid>
        <div style={box}>
          <span style={boxTitle}>{t(`${KEY}.description.label`)}</span>
          <div style={{ fontSize: 14, lineHeight: 1.5, color: MUTED, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {description}
          </div>
        </div>
        <div style={box}>
          <span style={boxTitle}>{t(`${KEY}.review.copied`)}</span>
          <span style={{ fontSize: 14, lineHeight: 1.43, color: MUTED }}>{t(`${KEY}.review.copiedBody`, { count: imageCount })}</span>
        </div>
      </Grid>
      <Grid item xs={12} md={5}>
        <div style={sidePanel}>
          {!isMobile && previewCard(120)}
          {acceptance}
        </div>
      </Grid>
    </Grid>
  )

  const successView = published && (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 560, padding: '8px 0 8px' }}>
      <CheckCircleOutlineIcon size={48} color={GREEN} />
      <div>
        <Typography variant='h5' style={{ marginBottom: 8 }}>
          {t(published.alreadyLinked ? `${KEY}.success.alreadyTitle` : `${KEY}.success.title`)}
        </Typography>
        <Typography variant='body1'>
          {t(published.alreadyLinked ? `${KEY}.success.alreadyBody` : `${KEY}.success.body`)}
        </Typography>
      </div>
      {published.code && (
        <a
          href={getFiksumyyntiListingUrl(published.code)}
          target='_blank'
          rel='noopener noreferrer'
          style={{ display: 'flex', alignItems: 'center', gap: 14, border: '1px solid rgba(0,0,0,.125)', borderRadius: 4, padding: 10, textDecoration: 'none', color: '#000' }}
        >
          {coverUrl ? (
            <img src={coverUrl} alt='' style={{ width: 84, height: 56, objectFit: 'cover', flex: 'none' }} />
          ) : (
            <span style={{ width: 84, height: 56, background: '#E7ECEF', flex: 'none' }} />
          )}
          <span style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontFamily: 'CircularBold', fontSize: 16 }}>{`${streetLabel} · ${formatEur(salePrice)}`}</span>
            <span style={{ fontSize: 14, color: MUTED }}>{t(`${KEY}.success.openListing`)}</span>
          </span>
          <OpenInNewIcon size={20} color={MUTED} />
        </a>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <span style={sideTitle}>{t(`${KEY}.success.nextTitle`)}</span>
        {['next1', 'next2', 'next3'].map((key, index) => (
          <div key={key} style={{ display: 'flex', gap: 12 }}>
            <span style={{ width: 24, height: 24, borderRadius: 12, background: GREEN, color: '#fff', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
              {index + 1}
            </span>
            <Typography variant='body1'>{t(`${KEY}.success.${key}`)}</Typography>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 8 }}>
        <Button color='primary' onClick={handleClose}>{t(`${KEY}.close`)}</Button>
        <Button variant='contained' color='primary' href={fmUrl} target='_blank' rel='noopener noreferrer'>
          {t(`${KEY}.success.openFm`)}
        </Button>
      </div>
    </div>
  )

  const stepHeader = isMobile ? (
    <div style={{ marginBottom: 20 }}>
      <LinearProgress variant='determinate' value={((step + 1) / STEP_KEYS.length) * 100} />
      <Typography variant='body2' style={{ color: MUTED, marginTop: 12 }}>
        {`${t(`${KEY}.stepOf`, { step: step + 1, total: STEP_KEYS.length })} · ${t(`${KEY}.steps.${STEP_KEYS[step]}`)}`}
      </Typography>
    </div>
  ) : (
    <Stepper activeStep={step} style={{ padding: '8px 0 24px' }}>
      {STEP_KEYS.map(key => (
        <Step key={key}>
          <StepLabel>{t(`${KEY}.steps.${key}`)}</StepLabel>
        </Step>
      ))}
    </Stepper>
  )

  const footer = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 24 }}>
      {step === 0 ? (
        <Button color='primary' onClick={handleClose}>{t(`${KEY}.cancel`)}</Button>
      ) : (
        <Button color='primary' onClick={() => setStep(step - 1)} disabled={submitting}>{t(`${KEY}.back`)}</Button>
      )}
      {step < STEP_KEYS.length - 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {!isMobile && (
            <Typography variant='body2' style={{ color: MUTED }}>
              {t(`${KEY}.stepOf`, { step: step + 1, total: STEP_KEYS.length })}
            </Typography>
          )}
          <Button
            variant='contained'
            color='primary'
            onClick={goNext}
            disabled={step === 2 && descriptionLoading}
            style={{ padding: '6px 22px' }}
          >
            {t(`${KEY}.next`)}
          </Button>
        </div>
      )}
    </div>
  )

  const steps = [priceStep, companyStep, descriptionStep, reviewStep]

  return (
    <Dialog
      dialogId={SELL_ON_FIKSUMYYNTI_DIALOG_ID}
      title={t(`${KEY}.title`)}
      maxWidth='md'
      noOpenDialogButton
      fullScreenDialogMobile
      onClose={onClose}
      Content={
        published ? (
          successView
        ) : (
          <div style={{ paddingRight: isMobile ? 8 : 0 }}>
            {stepHeader}
            {steps[step]}
            {footer}
          </div>
        )
      }
    />
  )
}

export default SellOnFiksumyyntiDialog
